import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, UserPlus, ExternalLink, Handshake, RefreshCw, UserRound, LogIn } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * KakoFollowCheck — passo 1 do cadastro: identifica a conta (avatar + apelido) e
 * confere a relação com a MoedasOnline. O código só é liberado para amigos mútuos.
 */
export const KakoFollowCheck = ({ kakoId, onReady, onGoLogin, ctaClass = '' }) => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [erro, setErro] = useState('');

  const verificar = async () => {
    setBusy(true); setErro(''); setRes(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/kako-auth/follow-status`, {
        kako_id: (kakoId || '').replace(/\D/g, ''),
      });
      setRes(r.data);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Não foi possível verificar agora. Tente de novo.');
    } finally {
      setBusy(false);
    }
  };

  const rel = res?.relation;
  const user = res?.user;
  const jaTemConta = res?.already_registered === true;
  const precisaSeguir = !jaTemConta && rel && rel.follows_us === false;
  // relação indisponível não bloqueia; conta existente sempre bloqueia
  const liberado = res && !jaTemConta && (!rel || rel.follows_us === true);

  return (
    <div className="space-y-3">
      {!res && (
        <Button className={`w-full ${ctaClass}`} disabled={!kakoId || busy}
          data-testid="register-check-account" onClick={verificar}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <UserRound className="mr-2 h-4 w-4" />}
          Verificar conta
        </Button>
      )}

      {erro && <p className="text-sm text-red-600" data-testid="register-check-error">{erro}</p>}

      {user && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5"
          data-testid="register-check-user">
          {user.avatar
            ? <img src={user.avatar} alt="" data-testid="register-check-avatar"
                className="h-11 w-11 shrink-0 rounded-full border-2 border-orange-300 object-cover" />
            : <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-orange-300 bg-orange-50 text-orange-400">
                <UserRound className="h-5 w-5" />
              </span>}
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-slate-800" data-testid="register-check-nickname">
              {user.nickname}
            </p>
            <p className="text-xs text-slate-500">KAKO ID {user.id}</p>
          </div>
        </div>
      )}

      {jaTemConta && (
        <div className="rounded-xl border border-blue-300 bg-blue-50 p-3 text-sm"
          data-testid="register-already-registered">
          <p className="flex items-center gap-2 font-bold text-blue-800">
            <LogIn className="h-4 w-4" /> Você já tem conta com esse KAKO ID
          </p>
          <p className="mt-1 text-gray-700">
            Não precisa criar de novo: entre com sua senha. Se esqueceu, use
            "Esqueci minha senha" na aba Entrar.
          </p>
          <Button className={`mt-2 w-full ${ctaClass}`} data-testid="register-go-login"
            onClick={() => onGoLogin?.(user)}>
            Ir para o login
          </Button>
        </div>
      )}

      {precisaSeguir && (
        <div className="rounded-xl border border-orange-300 bg-orange-50 p-3 text-sm"
          data-testid="register-follow-required">
          <p className="flex items-center gap-2 font-bold text-orange-700">
            <UserPlus className="h-4 w-4" /> Você ainda não segue a MoedasOnline
          </p>
          <p className="mt-1 text-gray-700">
            O código chega pelo chat do KAKO, e o app só entrega mensagem entre amigos.
            Abra o perfil, toque em <b>Seguir</b> e volte aqui para verificar de novo.
          </p>
          <a href={res.profile_url} target="_blank" rel="noopener noreferrer"
            data-testid="register-follow-link"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-orange-500 px-3 py-2 font-bold text-white hover:bg-orange-600">
            <ExternalLink className="h-4 w-4" /> Abrir perfil da MoedasOnline
          </a>
          <Button variant="outline" className="mt-2 w-full" disabled={busy}
            data-testid="register-recheck" onClick={verificar}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <RefreshCw className="mr-2 h-4 w-4" />}
            Já segui — verificar novamente
          </Button>
        </div>
      )}

      {liberado && (
        <>
          <p className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 p-2.5 text-sm font-bold text-green-700"
            data-testid="register-mutual-ok">
            <Handshake className="h-4 w-4" />
            {rel?.mutual ? 'Amigos mútuos no KAKO ✓' : 'Você já segue a MoedasOnline ✓'}
          </p>
          <Button className={`w-full ${ctaClass}`} data-testid="register-send-code"
            onClick={() => onReady?.(user)}>
            Enviar código no chat do app
          </Button>
        </>
      )}
    </div>
  );
};

export default KakoFollowCheck;
